import {Controller, FieldError, useFormContext, useWatch, Validate} from "react-hook-form";
import {OfferFormInput} from "./OfferForm";
import {Referral} from "../../referral";
import {Observable} from "rxjs";
import {HoursInput} from "./HoursInput";
import React, {useCallback, useEffect} from "react";
import DatePicker, {ReactDatePickerProps} from "react-datepicker";
import {differenceInBusinessDays, parseISO} from "date-fns";
import {DatePickerCustomInput, ESBA_DATE_FORMAT, ValidationMessage} from "../../common";
import {useObservable} from "../../rxsupport";
import {isEmpty} from "lodash";
import {TextareaField} from "evergreen-ui";
import {OfferHoursDisplayCondition} from "../domain/offer";

type MinMaxArray = [number | null, number | null];

type OfferedHoursMinMax = {
    careMid: MinMaxArray;
    careHigh: MinMaxArray;
    direct: MinMaxArray;
    mid: MinMaxArray;
    high: MinMaxArray;
    group: MinMaxArray;
}

function extractMinMaxHours(referral: Referral): OfferedHoursMinMax {
    const getMinMax = (beneficial: number | null, optimal: number | null): MinMaxArray => {
        const min = beneficial ?? null;
        const max = optimal ?? null;
        return (min === null || min === 0) && max !== null ? [max, max] : [min, max];
    };

    return {
        careMid: getMinMax(referral.careMediatedMidMinHours, referral.careMediatedMidHours),
        careHigh: getMinMax(referral.careMediatedHighMinHours, referral.careMediatedHighHours),
        direct: getMinMax(referral.practDirectBeneficialHours, referral.practDirectOptimalHours),
        mid: getMinMax(referral.practMidBeneficialHours, referral.practMidOptimalHours),
        high: getMinMax(referral.practHighBeneficialHours, referral.practHighOptimalHours),
        group: getMinMax(referral.practGroupBeneficialHours, referral.practGroupOptimalHours),
    };
}

type OfferStaffingPlanInputProps = {
    referral: Referral;
    isSubmitting$: Observable<boolean>;
    hoursCond: OfferHoursDisplayCondition;
}
export const OfferStaffingPlanInput = ({
                                           referral,
                                           hoursCond,
                                           isSubmitting$,
                                       }: OfferStaffingPlanInputProps) => {
    const minMaxHours = extractMinMaxHours(referral);

    const {control, setValue} = useFormContext<OfferFormInput>();

    const careMidHours = useWatch<OfferFormInput, 'caregiverMediatedMidLevelHours'>({
        control,
        name: 'caregiverMediatedMidLevelHours'
    });
    const careHighHours = useWatch<OfferFormInput, 'caregiverMediatedHighLevelHours'>({
        control,
        name: 'caregiverMediatedHighLevelHours'
    });
    const directCareHours = useWatch<OfferFormInput, 'directCareHours'>({control, name: 'directCareHours'});
    const midLevelHours = useWatch<OfferFormInput, 'midLevelSupervisionHours'>({
        control,
        name: 'midLevelSupervisionHours'
    });
    const highLevelHours = useWatch<OfferFormInput, 'highLevelSupervisionHours'>({
        control,
        name: 'highLevelSupervisionHours'
    });
    const groupHours = useWatch<OfferFormInput, 'groupCareHours'>({control, name: 'groupCareHours'});

    const needStaffingPlanPract = [
            {condition: hoursCond.hasDirectHours && directCareHours, min: minMaxHours.direct[0], hours: directCareHours},
            {condition: hoursCond.hasMidHours && midLevelHours, min: minMaxHours.mid[0], hours: midLevelHours},
            {condition: hoursCond.hasHighHours && highLevelHours, min: minMaxHours.high[0], hours: highLevelHours},
            {condition: hoursCond.hasGroupHours && groupHours, min: minMaxHours.group[0], hours: groupHours}
        ].some(({condition, min, hours}) =>
            condition &&
            min &&
            parseFloat(hours ?? '') < min)
        && [
            ...(hoursCond.hasMidHours ? [midLevelHours] : []),
            ...(hoursCond.hasHighHours ? [highLevelHours] : []),
            ...(hoursCond.hasGroupHours ? [groupHours] : []),
            ...(hoursCond.hasDirectHours ? [directCareHours] : []),
        ].every(hours => !isEmpty(hours));

    const needStaffingPlanCare = [
            {
                condition: hoursCond.hasCareMediatedMidHours && careMidHours,
                min: minMaxHours.careMid[0],
                hours: careMidHours
            },
            {
                condition: hoursCond.hasCareMediatedHighHours && careHighHours,
                min: minMaxHours.careHigh[0],
                hours: careHighHours
            },
        ].some(({condition, min, hours}) =>
            condition &&
            min &&
            parseFloat(hours ?? '') < min)
        && [
            ...(hoursCond.hasCareMediatedMidHours ? [careMidHours] : []),
            ...(hoursCond.hasCareMediatedHighHours ? [careHighHours] : [])
        ].every(hours => !isEmpty(hours));

    const needStaffingPlan = needStaffingPlanPract || needStaffingPlanCare;

    useEffect(() => {
        if (!needStaffingPlan) {
            setValue('draftStaffingPlan', null);
        }
        if (needStaffingPlanCare && !needStaffingPlanPract) {
            setValue('draftStaffingPlan.offeredParaprofessionalHours', null);
            setValue('draftStaffingPlan.offeredGroupCareHours', null);
        }
    }, [needStaffingPlan, setValue, needStaffingPlanCare, needStaffingPlanPract]);

    return (
        <div>
            {needStaffingPlan && (
                <div className="border-2 p-2 mt-4 w-7/12 grid grid-cols-3">
                    <div className="col-span-3 text-xs font-bold text-red-700 pb-4">
                        Your offer does not meet the clinically recommended hours. Please include a “Staffing Plan”
                        indicating how staffing will be addressed
                    </div>
                    <FullOfferStartDateInput isSubmitting$={isSubmitting$}/>

                    {hoursCond.hasDirectHours && needStaffingPlanPract && (
                        <div className={'col-span-3'}>
                            <HoursInput
                                width='32em'
                                name="draftStaffingPlan.offeredParaprofessionalHours"
                                label="Paraprofessional Hours"
                                isRequired={true}
                                isSubmitting$={isSubmitting$}
                                twoDecimalSupport
                                minValue={minMaxHours.direct[0] ?? NaN}
                                maxValue={minMaxHours.direct[1] ?? NaN}
                            />
                        </div>
                    )}
                    {
                        (hoursCond.hasMidHours || hoursCond.hasCareMediatedMidHours) &&
                        <div className={'col-span-3'}>
                            <HoursInput
                                width='32em'
                                name="draftStaffingPlan.offeredMidLevelHours"
                                label="Mid Level Hours"
                                isRequired={true}
                                isSubmitting$={isSubmitting$}
                                twoDecimalSupport
                                minValue={
                                    ((needStaffingPlanCare && !needStaffingPlanPract) ?
                                        minMaxHours.careMid[0] : minMaxHours.mid[0]) ?? NaN
                                }
                                maxValue={
                                    ((needStaffingPlanCare && !needStaffingPlanPract) ?
                                        minMaxHours.careMid[1] : minMaxHours.mid[1]) ?? NaN
                                }
                            />
                        </div>
                    }
                    {
                        (hoursCond.hasHighHours || hoursCond.hasCareMediatedHighHours) &&
                        <div className={'col-span-3'}>
                            <HoursInput
                                width='32em'
                                name="draftStaffingPlan.offeredHighLevelHours"
                                label="High Level Hours"
                                isRequired={true}
                                isSubmitting$={isSubmitting$}
                                twoDecimalSupport
                                minValue={
                                    ((needStaffingPlanCare && !needStaffingPlanPract) ?
                                        minMaxHours.careHigh[0] : minMaxHours.high[0]) ?? NaN
                                }
                                maxValue={
                                    ((needStaffingPlanCare && !needStaffingPlanPract) ?
                                        minMaxHours.careHigh[1] : minMaxHours.high[1]) ?? NaN
                                }
                            />
                        </div>
                    }
                    {hoursCond.hasGroupHours && needStaffingPlanPract && (
                        <div className={'col-span-3'}>
                            <HoursInput
                                width='32em'
                                name="draftStaffingPlan.offeredGroupCareHours"
                                label="Group Care Hours"
                                isRequired={true}
                                isSubmitting$={isSubmitting$}
                                twoDecimalSupport
                                minValue={minMaxHours.group[0] ?? NaN}
                                maxValue={minMaxHours.group[1] ?? NaN}
                            />
                        </div>
                    )}
                    <div className={'col-span-2'}>
                        <CommentInput isSubmitting$={isSubmitting$}/>
                    </div>
                </div>
            )}
        </div>);
}


type FullOfferStartDateProps = {
    isSubmitting$: Observable<boolean>;
} & Omit<ReactDatePickerProps, 'onChange'>;
const FullOfferStartDateInput = ({isSubmitting$, ...datePickerProps}: FullOfferStartDateProps) => {
    const {
        control,
        formState: {
            errors
        },
    } = useFormContext<OfferFormInput>();
    const sending = useObservable(isSubmitting$, false);

    return (
        <div className="col-span-3">
            <Controller
                name='draftStaffingPlan.fullOfferStartDate'
                control={control}
                rules={{required: "This field required."}}
                render={({field: {onChange, onBlur, value: isoValueStr}}) => <DatePicker
                    {...datePickerProps}
                    disabled={sending}
                    onChange={(newValue: Date | null) => onChange(newValue?.toISOString() || null)}
                    onBlur={onBlur}
                    selected={isoValueStr ? parseISO(isoValueStr) : undefined}
                    dateFormat={ESBA_DATE_FORMAT}
                    customInput={
                        <DatePickerCustomInput
                            width='32em'
                            label='Full Scheduled Offer Start Date'
                            fieldName='draftStaffingPlan.fullOfferStartDate'
                            // @ts-ignore
                            error={errors?.draftStaffingPlan?.fullOfferStartDate}
                            value={isoValueStr as any}
                        />
                    }
                />}
            />
        </div>
    );
}

type ReviewedAndApprovedByClientFamilyDateInputProps = {
    isSubmitting$: Observable<boolean>;
} & Omit<ReactDatePickerProps, 'onChange'>;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ReviewedAndApprovedByClientFamilyDateInput = ({
                                                        isSubmitting$,
                                                        ...datePickerProps
                                                    }: ReviewedAndApprovedByClientFamilyDateInputProps) => {
    const {
        control,
        formState: {
            errors
        },
    } = useFormContext<OfferFormInput>();
    const sending = useObservable(isSubmitting$, false);

    return (
        <div className="col-span-3">
            <Controller
                name='draftStaffingPlan.reviewedAndApprovedByClientFamilyDate'
                control={control}
                rules={{required: "This field required."}}
                render={({field: {onChange, onBlur, value: isoValueStr}}) => <DatePicker
                    {...datePickerProps}
                    disabled={sending}
                    onChange={(newValue: Date | null) => onChange(newValue?.toISOString() || null)}
                    onBlur={onBlur}
                    selected={isoValueStr ? parseISO(isoValueStr) : undefined}
                    dateFormat={ESBA_DATE_FORMAT}
                    customInput={
                        <DatePickerCustomInput
                            width='32em'
                            label='Date Staffing Plan Reviewed by Client Family'
                            fieldName='draftStaffingPlan.reviewedAndApprovedByClientFamilyDate'
                            showDifferenceInBusinessDays={false}
                            // @ts-ignore
                            error={errors?.draftStaffingPlan?.reviewedAndApprovedByClientFamilyDate}
                            value={isoValueStr as any}
                        />
                    }
                />}
            />
        </div>
    );
}

type CommentInputProps = {
    isSubmitting$: Observable<boolean>;
};

const CommentInput = ({isSubmitting$}: CommentInputProps) => {
    const {register, formState: {errors}, watch} = useFormContext<OfferFormInput>();
    const offerStartDate = watch('draftStaffingPlan.fullOfferStartDate');
    const requireRationaleForOfferStartDate: Validate<string> = useCallback((comments) => {
        const startDate = offerStartDate || '';
        if (startDate.length > 0) {
            const bizDays = differenceInBusinessDays(new Date(startDate), new Date());
            if (bizDays > 10) {
                const rationale = (comments || '').trim();
                return rationale.length > 0 || 'Staffing Plan Rationale is required when Full-offer Start Date is more than 10 business days from today';
            }
        }
        return true;
    }, [offerStartDate]);

    const sending = useObservable(isSubmitting$, false);

    return (
        <TextareaField
            id='draftStaffingPlan.comments'
            {...register('draftStaffingPlan.comments', {validate: {custom: requireRationaleForOfferStartDate}})}
            label='Staffing Plan Comments'
            maxWidth='36em'
            maxLength={2048}
            disabled={sending}
            validationMessage={
                <ValidationMessage
                    // @ts-ignore
                    error={errors?.draftStaffingPlan?.comments as FieldError | undefined}
                />
            }
        />
    );
};
